/**
 * Role names → permission templates. Sits directly under the bundles × verbs
 * matrix so an admin who just read a template as a column can immediately see
 * which role *names* resolve to it.
 *
 * Three vocabularies exist and only two of them are joined here. Role names
 * live in delivery.tenant_enum_values (domain='role_type'); permission
 * templates are the authz role bundles the matrix above charts. `roleBundleKey`
 * is the join. Nothing on this panel grants access — attaching a template to a
 * name is configuration, and no grant is minted by saving a row.
 *
 * Two empty values are meaningful, and both read identically to "unset" if
 * rendered as a blank cell, so neither ever is:
 *
 *   - empty `businessUnitId` = the name is offered in EVERY business unit,
 *     not "no business unit". Rendered "All business units".
 *   - empty `roleBundleKey` = the name grants nothing. Rendered
 *     "No template — informational only".
 *
 * Aliasing is stated in words on the row ("Also: …") rather than left to be
 * inferred by scanning the table: two names deliberately sharing one template
 * (Program Lead and Task Order Lead both being Project Manager) is the thing
 * the panel exists to reassure about, and two teams slowly diverging names
 * that were meant to be identical is the thing it exists to prevent.
 *
 * Seeded (`isSystem`) rows may have a template attached — that is the whole
 * point of the feature — but their `code` is load-bearing and cannot change,
 * so the edit form shows it read-only instead of silently ignoring an edit.
 *
 * A seeded row is NOT edited in place: it lives on the shared `_system` tenant
 * that every tenant reads, so pangaea copies it into a tenant-owned row
 * carrying the chosen template and leaves the shared one alone. The form says
 * so rather than letting the admin believe they changed the shared catalog —
 * a copy that is offered everywhere replaces the seeded row in this list, so
 * nothing on screen would otherwise reveal that a copy was made. A copy scoped
 * to one business unit is a different offering and BOTH rows stay listed; Edit
 * on either of them edits the copy, which is why the form defaults from it.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { EntityFormDialog } from '../../Delivery/_shared/components/EntityFormDialog';
import { EntityTable } from '../../Delivery/_shared/components/EntityTable';
import { roleNameSelfService, useBusinessUnitOptions, useListBundles, useRoleNames, useUpsertRoleName } from '../Access/hooks';
// Radix Select forbids an empty-string item value, but empty is exactly what
// both of these fields must be able to submit. So each meaningful "empty" is a
// real, named option carrying a sentinel, mapped back to '' on submit.
const NO_TEMPLATE = '__no_template__';
const ALL_BUSINESS_UNITS = '__all_business_units__';
export const NO_TEMPLATE_TEXT = 'No template — informational only';
export const ALL_BUSINESS_UNITS_TEXT = 'All business units';
/** Human name for a template key; falls back to the raw key if the catalog lacks it. */ export function bundleLabel(key, bundles) {
    if (!key) return NO_TEMPLATE_TEXT;
    const hit = bundles.find((b)=>b.key === key);
    return hit?.displayName || key;
}
/** BU name for a scope id; empty id means every BU, never "unassigned". */ export function scopeLabel(businessUnitId, units) {
    if (!businessUnitId) return ALL_BUSINESS_UNITS_TEXT;
    return units.find((u)=>u.id === businessUnitId)?.name || businessUnitId;
}
/**
 * Names sharing each non-empty template key, so a row can name its aliases
 * without the reader diffing the table. Keyed by template; empty-template rows
 * are excluded — they share nothing because they grant nothing.
 */ export function aliasesByTemplate(rows) {
    const byKey = new Map();
    for (const row of rows){
        if (!row.roleBundleKey) continue;
        const list = byKey.get(row.roleBundleKey);
        if (list) list.push(row);
        else byKey.set(row.roleBundleKey, [
            row
        ]);
    }
    return byKey;
}
export function RoleNamesPanel() {
    const { data: rows, isLoading, error, refetch } = useRoleNames();
    const { data: bundles } = useListBundles(true);
    const { data: units } = useBusinessUnitOptions();
    const upsert = useUpsertRoleName();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const allRows = rows ?? [];
    const catalog = bundles ?? [];
    const businessUnits = units ?? [];
    const aliases = useMemo(()=>aliasesByTemplate(allRows), [
        allRows
    ]);
    // The tenant's own copy of the seeded row being edited, when one exists.
    //
    // A seeded name the tenant has already overridden is TWO rows in this list:
    // the shared `_system` one and the tenant's copy. Both are listed on purpose —
    // this panel asks for no business unit, and an offering is
    // (code, business_unit_id), so a copy scoped to one BU is a genuinely
    // different offering from the company-wide seeded row and hiding it would
    // hide the override from the admin.
    //
    // But Edit on the SHARED row does not write the shared row: pangaea's
    // copy-on-write resolves the write to the copy by (tenant, domain, code). So
    // the form has to default from the copy, not from the shared row — otherwise
    // an admin who opens the seeded row and saves without touching anything
    // submits "All business units" over a copy they had deliberately scoped to
    // one BU, silently widening it. Same trap for the copy's template and label,
    // and for the fields handleSubmit echoes back.
    const tenantCopy = useMemo(()=>{
        if (!editing?.isSystem) return null;
        return allRows.find((r)=>!r.isSystem && r.code === editing.code) ?? null;
    }, [
        allRows,
        editing
    ]);
    // What the form reads. `editing` still decides whether the code is locked —
    // that's a property of the row the admin clicked, and the copy carries
    // isSystem=false by construction.
    const formSource = tenantCopy ?? editing;
    const columns = [
        {
            key: 'code',
            header: 'Code',
            render: (row)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-mono text-xs",
                            children: row.code
                        }),
                        row.isSystem && /*#__PURE__*/ _jsx(Badge, {
                            variant: "secondary",
                            className: "text-[10px]",
                            children: "Seeded"
                        })
                    ]
                })
        },
        {
            key: 'displayLabel',
            header: 'Name'
        },
        {
            key: 'roleBundleKey',
            header: 'Permission template',
            render: (row)=>{
                // Every other name on the same template, named outright.
                const shared = (aliases.get(row.roleBundleKey) ?? []).filter((r)=>r.id !== row.id);
                return /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.adminConsole.roleNameTemplate,
                    children: [
                        row.roleBundleKey ? /*#__PURE__*/ _jsx("span", {
                            children: bundleLabel(row.roleBundleKey, catalog)
                        }) : /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: NO_TEMPLATE_TEXT
                        }),
                        shared.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-muted-foreground",
                            "data-testid": Testids.adminConsole.roleNameAlias,
                            children: [
                                "Also: ",
                                shared.map((r)=>r.displayLabel || r.code).join(', ')
                            ]
                        })
                    ]
                });
            }
        },
        {
            key: 'businessUnitId',
            header: 'Offered in',
            render: (row)=>/*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.adminConsole.roleNameScope,
                    className: row.businessUnitId ? undefined : 'text-muted-foreground',
                    children: scopeLabel(row.businessUnitId, businessUnits)
                })
        }
    ];
    const templateOptions = useMemo(()=>[
            {
                value: NO_TEMPLATE,
                label: NO_TEMPLATE_TEXT
            },
            ...catalog.map((b)=>({
                    value: b.key,
                    label: b.displayName || b.key
                }))
        ], [
        catalog
    ]);
    const scopeOptions = useMemo(()=>[
            {
                value: ALL_BUSINESS_UNITS,
                label: ALL_BUSINESS_UNITS_TEXT
            },
            ...businessUnits.map((u)=>({
                    value: u.id,
                    label: u.name
                }))
        ], [
        businessUnits
    ]);
    // A seeded row drops the code field entirely rather than rendering a
    // disabled one the form would still submit — the locked value is shown
    // above instead, so the restriction is visible.
    const codeLocked = !!editing?.isSystem;
    const fields = useMemo(()=>{
        const codeField = {
            kind: 'text',
            name: 'code',
            label: 'Code',
            required: true,
            maxLength: 60,
            helpText: 'Stable identifier stored on the role. Cannot change once other data references it.'
        };
        const rest = [
            {
                kind: 'text',
                name: 'displayLabel',
                label: 'Display label',
                required: true,
                maxLength: 120
            },
            {
                kind: 'select',
                name: 'roleBundleKey',
                label: 'Permission template',
                options: templateOptions,
                helpText: 'The permission set this name resolves to. Several names may share one template — that is how two titles grant identical access.'
            },
            {
                kind: 'select',
                name: 'businessUnitId',
                label: 'Offered in',
                options: scopeOptions,
                helpText: 'Pick a business unit to offer the name only there, or leave it available everywhere.'
            }
        ];
        return codeLocked ? rest : [
            codeField,
            ...rest
        ];
    }, [
        codeLocked,
        templateOptions,
        scopeOptions
    ]);
    const openCreate = ()=>{
        setEditing(null);
        setFormOpen(true);
    };
    const openEdit = (row)=>{
        setEditing(row);
        setFormOpen(true);
    };
    const handleSubmit = async (values)=>{
        await upsert.mutateAsync({
            id: editing?.id ?? '',
            // Seeded codes are never taken from the form — the field isn't offered.
            code: codeLocked ? editing?.code ?? '' : (values.code ?? '').trim(),
            displayLabel: values.displayLabel ?? '',
            roleBundleKey: values.roleBundleKey === NO_TEMPLATE ? '' : values.roleBundleKey ?? '',
            businessUnitId: values.businessUnitId === ALL_BUSINESS_UNITS ? '' : values.businessUnitId ?? '',
            // Not edited here, but the upsert replaces the row — echo them back, from
            // the tenant's copy when there is one so a save doesn't reset them to the
            // seeded values.
            description: formSource?.description ?? '',
            sortOrder: formSource?.sortOrder ?? 0,
            isLeadership: formSource?.isLeadership ?? false,
            selfService: roleNameSelfService(formSource?.metadataJson ?? '')
        });
        setFormOpen(false);
    };
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": Testids.adminConsole.roleNamesPanel,
        children: [
            /*#__PURE__*/ _jsxs(CardHeader, {
                className: "flex flex-row items-start justify-between gap-3 pb-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(CardTitle, {
                                className: "text-sm",
                                children: "Which role names use which template"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 max-w-2xl text-xs text-muted-foreground",
                                children: "A role name is what people pick when staffing a program; the template above is what it actually grants. Names may deliberately share a template — when they do, each row says so. A name with no template is a label only and grants nothing."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "sm",
                        onClick: openCreate,
                        "data-testid": Testids.adminConsole.roleNameCreate,
                        children: "New role name"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CardContent, {
                children: /*#__PURE__*/ _jsx(EntityTable, {
                    data: allRows,
                    columns: columns,
                    testid: Testids.adminConsole.roleNamesTable,
                    rowTestid: Testids.adminConsole.roleNameRow,
                    rowKey: (r)=>r.id,
                    isLoading: isLoading,
                    error: error,
                    onRetry: ()=>refetch(),
                    rowActions: (row)=>/*#__PURE__*/ _jsx(Button, {
                            size: "sm",
                            variant: "ghost",
                            onClick: ()=>openEdit(row),
                            "data-testid": Testids.adminConsole.roleNameEdit,
                            children: "Edit"
                        })
                })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: editing ? 'Edit role name' : 'Create role name',
                description: codeLocked ? 'Saving creates your organisation’s own version of this seeded name — the shared one is left as it is. Attaching a template is configuration; it grants nobody access on its own.' : 'Attaching a template is configuration — it grants nobody access on its own.',
                fields: fields,
                topContent: codeLocked ? /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1.5",
                    "data-testid": Testids.adminConsole.roleNameCodeLocked,
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm font-medium",
                            children: "Code"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "font-mono text-sm",
                            children: editing?.code
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "Seeded role name — the code cannot be changed. Its template and business unit can."
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: "This name is shared with every organisation, so saving creates a version of it owned by yours. The shared one is not modified, and your version replaces it everywhere this name is offered."
                        })
                    ]
                }) : undefined,
                defaultValues: {
                    code: editing?.code ?? '',
                    displayLabel: formSource?.displayLabel ?? '',
                    roleBundleKey: formSource?.roleBundleKey || NO_TEMPLATE,
                    businessUnitId: formSource?.businessUnitId || ALL_BUSINESS_UNITS
                },
                onSubmit: handleSubmit,
                isSubmitting: upsert.isPending
            })
        ]
    });
}
